/**
 * @file index.tsx
 *
 * @fileoverview Registration information section.
 */
import { Text } from '~/vcl/atoms/13-text/03-text';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import EducationIcon from '~/jobiqo/components/Icons/EducationIcon';
import { LockIcon } from '~/vcl/icons/Lock';
import { EnvelopeIcon } from '~/vcl/icons/Envelope';
import { OfficeIcon } from '~/vcl/icons/Office';
import { BriefcaseIcon } from '~/vcl/icons/Briefcase';
import { PaymentIcon } from '~/vcl/icons/Payment';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { Badge } from '~/vcl/atoms/03-badge';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { FlexItem } from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import { Slottable } from '~/jobiqo/components/Slots/interfaces';
import Link from '~/jobiqo/components/Link';

export interface UserRegisterInfoSlotProps {
  /**
   * Flag for applicant view.
   */
  isApplicant: Slottable<boolean>;
}

// Disable react/jsx-key check for info configurations.
// Array elements are rendered as nested components.
/* eslint-disable react/jsx-key */
const applicantInfo = {
  steps: [
    <FormattedMessage
      id="register_info_step_1_applicant"
      defaultMessage="Create your account by providing your email address and password. You can change both later."
    />,
    <FormattedMessage
      id="register_info_step_2_applicant"
      defaultMessage="Verify your account by going to your email inbox and click the verification link in your welcome email."
    />,
    <FormattedMessage
      id="register_info_step_3_applicant"
      defaultMessage="Enter your resume information in your dashboard to create your online CV so recruiters can find you more easily!"
    />,
  ],
  title: (
    <FormattedMessage
      id="register_info_title_applicant"
      defaultMessage="Applicant registration"
    />
  ),
  stepsTitle: (
    <FormattedMessage
      id="register_info_steps_title_applicant"
      defaultMessage="Register your account in 3 simple registration steps"
    />
  ),
  icons: [
    <LockIcon title="Lock" color="primary" height="40" width="40" />,
    <EnvelopeIcon title="Envelope" color="primary" height="40" width="40" />,
    <EducationIcon title="Graduation" color="primary" height="40" width="40" />,
  ],
};

const recruiterInfo = {
  steps: [
    <FormattedMessage
      id="register_info_step_1_recruiter"
      defaultMessage="Create your account by providing your email address account and password. You can change both later."
    />,
    <FormattedMessage
      id="register_info_step_2_recruiter"
      defaultMessage="Verify your account by going to your email inbox and click the verification link in your welcome email. Without verification, you won't be able to post jobs!"
    />,
    <FormattedMessage
      id="register_info_step_3_recruiter"
      defaultMessage="Fill out all the information about your account, and also add it to the organization page as much information as you can - the logo and the website are required to proceed."
    />,
    <FormattedMessage
      id="register_info_step_4_recruiter"
      defaultMessage="Post a job either with a template, a PDF, or a link (provide a link that can be embedded in an iframe, otherwise it won’t work properly)."
    />,
    <FormattedMessage
      id="register_info_step_5_recruiter"
      defaultMessage="Proceed to the checkout and see your job being published!"
    />,
  ],
  title: (
    <FormattedMessage
      id="register_info_title_recruiter"
      defaultMessage="Recruiter registration"
    />
  ),
  stepsTitle: (
    <FormattedMessage
      id="register_info_steps_title_recruiter"
      defaultMessage="Post a job in 5 steps"
    />
  ),
  icons: [
    <LockIcon title="Lock" color="primary" height="40" width="40" />,
    <EnvelopeIcon title="Envelope" color="primary" height="40" width="40" />,
    <OfficeIcon title="Office" color="primary" height="40" width="40" />,
    <BriefcaseIcon title="Briefcase" color="primary" height="40" width="40" />,
    <PaymentIcon title="Payment" color="primary" height="40" width="40" />,
  ],
};
/* eslint-enable react/jsx-key */

export const UserRegisterInfoSlot = ({
  isApplicant,
}: UserRegisterInfoSlotProps) => {
  const { title, stepsTitle, steps, icons } = isApplicant
    ? applicantInfo
    : recruiterInfo;

  return (
    <Spacing padding={11}>
      <div>
        <Badge badgeStyle="secondaryVeryLight">{title}</Badge>
        <Text
          textAlign="left"
          color="gray8"
          fontWeight="bold"
          fontSize={5}
          lineHeight={5}
        >
          {stepsTitle}
        </Text>
      </div>
      <Spacing size="large" />
      <div>
        {steps.map((step, index) => (
          <div key={index}>
            <FlexItems
              key={index}
              alignItems="flex-start"
              flexWrap="nowrap"
              gap={6}
            >
              <FlexItem>{icons[index]}</FlexItem>
              <FlexItem>
                <Text marginTop="0" color="gray8" fontWeight="bold">
                  {step}
                </Text>
              </FlexItem>
            </FlexItems>
            <Spacing size="large" />
          </div>
        ))}
      </div>
      <Spacing />
      <Text>
        <FormattedMessage
          id="register_info_contact_us"
          defaultMessage="Do you have any questions or feedback about the registration? {link}"
          values={{
            link: (
              <Link href="/contact">
                <FormattedMessage
                  id="register_info_contact_us_link"
                  defaultMessage="Contact us!"
                />
              </Link>
            ),
          }}
        />
      </Text>
    </Spacing>
  );
};
